(() => {
  "use strict";

  const WB = window.Wishbone || {};
  const rootPath =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";
  const endpoint = (path) => `${rootPath}${path}`.replace(/\/+/g, "/");
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) =>
    Array.from(scope.querySelectorAll(selector));
  const string = (key, fallback = "") =>
    (WB.strings && WB.strings[key]) || fallback;

  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(WB.locale || "pt-BR", {
        style: "currency",
        currency:
          (window.Shopify &&
            window.Shopify.currency &&
            window.Shopify.currency.active) ||
          "BRL",
      }).format(value);
    } catch (_) {
      return `R$ ${value.toFixed(2).replace(".", ",")}`;
    }
  };

  const toast = (message, error = false) => {
    const region = $("#WbToastRegion");
    if (!region) return;
    const item = document.createElement("div");
    item.className = `toast${error ? " toast--error" : ""}`;
    item.textContent = message;
    region.appendChild(item);
    window.setTimeout(() => item.remove(), 4200);
  };

  const analytics = (name, data = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...data });
  };

  const readJson = (node) => {
    try {
      return JSON.parse(node && node.textContent ? node.textContent : "null");
    } catch (_) {
      return null;
    }
  };

  const minimumQuantity = () =>
    Math.max(30, Number(WB.settings && WB.settings.minQuantity) || 30);

  const minimumStockQuantity = () =>
    Math.max(
      1,
      Number(WB.settings && WB.settings.minimumStockQuantity) || 30,
    );

  const quantityRule = (raw = {}) => {
    const min = Math.max(1, Number(raw.min) || minimumQuantity());
    const step = Math.max(
      1,
      Number(raw.step) || Number(WB.settings && WB.settings.quantityStep) || 5,
    );
    const rawMax = Number(raw.max);
    const max = Number.isFinite(rawMax) && rawMax >= min ? rawMax : null;
    return { ...raw, min, max, step };
  };

  const quantityRuleFromDataset = (node) =>
    quantityRule({
      min: node && node.dataset.quantityMin,
      max: node && node.dataset.quantityMax,
      step: node && node.dataset.quantityStep,
    });

  const cartItemQuantityRule = (item) => {
    const properties = (item && item.properties) || {};
    return quantityRule({
      min: properties._wb_quantity_min,
      max: properties._wb_quantity_max,
      step: properties._wb_quantity_step,
    });
  };

  const normalizeQuantity = (raw, rawRule = {}) => {
    const rule = quantityRule(rawRule);
    const value = Number(raw);
    if (!Number.isFinite(value) || value < rule.min) return rule.min;
    let normalized =
      rule.min + Math.ceil((value - rule.min) / rule.step) * rule.step;
    if (rule.max !== null && normalized > rule.max)
      normalized =
        rule.min + Math.floor((rule.max - rule.min) / rule.step) * rule.step;
    return normalized;
  };

  const storefrontInventoryCache = new Map();

  const storefrontInventoryConfigured = () =>
    Boolean(
      WB.settings &&
        WB.settings.storefrontDomain &&
        WB.settings.storefrontAccessToken,
    );

  const storefrontVariantId = (id) =>
    `gid://shopify/ProductVariant/${String(id).replace(/\D/g, "")}`;

  async function fetchStorefrontInventory(variantIds, force = false) {
    const ids = [...new Set(variantIds.map(String).filter(Boolean))];
    if (!ids.length) return new Map();
    if (!storefrontInventoryConfigured())
      throw new Error("Storefront inventory is not configured");
    const requestedIds = force
      ? ids
      : ids.filter((id) => !storefrontInventoryCache.has(id));

    if (requestedIds.length) {
      const version = String(WB.settings.storefrontApiVersion || "2026-07");
      const domain = String(WB.settings.storefrontDomain)
        .replace(/^https?:\/\//, "")
        .replace(/\/$/, "");
      const response = await fetch(
        `https://${domain}/api/${version}/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": String(
              WB.settings.storefrontAccessToken,
            ),
          },
          body: JSON.stringify({
            query: `query VariantInventory($ids: [ID!]!) {
              nodes(ids: $ids) {
                ... on ProductVariant {
                  id
                  availableForSale
                  quantityAvailable
                }
              }
            }`,
            variables: { ids: requestedIds.map(storefrontVariantId) },
          }),
        },
      );
      const result = await response.json().catch(() => null);
      if (!response.ok || !result || result.errors)
        throw new Error("Storefront inventory request failed");

      const nodes = (result.data && result.data.nodes) || [];
      requestedIds.forEach((id, index) => {
        const node = nodes[index];
        const quantity =
          node && node.quantityAvailable !== null
            ? Number(node.quantityAvailable)
            : null;
        if (!node || !Number.isFinite(quantity))
          throw new Error("Storefront inventory is unavailable");
        storefrontInventoryCache.set(id, {
          availableForSale: node.availableForSale === true,
          quantityAvailable: quantity,
        });
      });
    }

    return new Map(ids.map((id) => [id, storefrontInventoryCache.get(id)]));
  }

  const applyStorefrontInventory = (variant, inventory) => {
    if (!variant || !inventory) return;
    variant.storefront_inventory_checked = true;
    variant.storefront_available = inventory.availableForSale;
    variant.storefront_quantity_available = inventory.quantityAvailable;
  };

  const variantHasMinimumStock = (variant, requiredQuantity = minimumStockQuantity()) =>
    Boolean(variant) &&
    variant.storefront_inventory_checked === true &&
    Number(variant.storefront_quantity_available) >= requiredQuantity;

  const variantIsPurchasable = (variant, requiredQuantity) =>
    Boolean(
      variant &&
        variant.storefront_available === true &&
        variantHasMinimumStock(variant, requiredQuantity),
    );

  const cartNoticeKey = "wb_cart_notice";

  function showPendingCartNotice() {
    let message = "";
    try {
      message = window.sessionStorage.getItem(cartNoticeKey) || "";
      if (message) window.sessionStorage.removeItem(cartNoticeKey);
    } catch (_) {
      return;
    }
    if (message) toast(message, true);
  }

  function initMinimumQuantityInputs(scope = document) {
    $$('input[type="number"][name="quantity"], input[type="number"][name="updates[]"]', scope).forEach((input) => {
      if (input.dataset.minimumQuantityReady) return;
      input.dataset.minimumQuantityReady = "true";
      const rule = quantityRuleFromDataset(input);
      input.min = String(rule.min);
      input.step = String(rule.step);
      if (rule.max !== null) input.max = String(rule.max);
      const commit = () => {
        input.value = String(normalizeQuantity(input.value, rule));
      };
      commit();
      input.addEventListener("change", commit);
      input.addEventListener("blur", commit);
      const form = input.form;
      if (form && !form.dataset.minimumQuantityReady) {
        form.dataset.minimumQuantityReady = "true";
        form.addEventListener("submit", () => {
          $$('input[type="number"][name="quantity"], input[type="number"][name="updates[]"]', form).forEach((quantityInput) => {
            quantityInput.value = String(
              normalizeQuantity(
                quantityInput.value,
                quantityRuleFromDataset(quantityInput),
              ),
            );
          });
        });
      }
    });
  }

  const phoneProfile = (locale = "") => {
    const language = String(locale).toLowerCase().split("-")[0];
    if (language === "en")
      return { countryCode: "1", minDigits: 10, maxDigits: 10, format: "us" };
    if (language === "es")
      return { countryCode: "34", minDigits: 9, maxDigits: 9, format: "es" };
    return { countryCode: "55", minDigits: 10, maxDigits: 11, format: "br" };
  };

  const phoneNationalDigits = (raw, profile) => {
    const value = String(raw || "");
    let digits = value.replace(/\D/g, "");
    const hasInternationalPrefix = value.trim().startsWith("+");
    if (
      digits.startsWith(profile.countryCode) &&
      (hasInternationalPrefix || digits.length > profile.maxDigits)
    )
      digits = digits.slice(profile.countryCode.length);
    return digits.slice(0, profile.maxDigits);
  };

  const formatPhone = (raw, profile) => {
    const digits = phoneNationalDigits(raw, profile);
    if (!digits) return "";

    if (profile.format === "us") {
      if (digits.length <= 3) return `+1 (${digits}`;
      if (digits.length <= 6)
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    if (profile.format === "es")
      return `+34 ${[digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)].filter(Boolean).join(" ")}`;

    if (digits.length <= 2) return `(${digits}`;
    const areaCode = digits.slice(0, 2);
    const subscriber = digits.slice(2);
    const firstGroupLength = subscriber.startsWith("9") ? 5 : 4;
    if (subscriber.length <= firstGroupLength)
      return `(${areaCode}) ${subscriber}`;
    return `(${areaCode}) ${subscriber.slice(0, firstGroupLength)}-${subscriber.slice(firstGroupLength)}`;
  };

  function initHeader(scope = document) {
    const header = $("[data-site-header]", scope) || $("[data-site-header]");
    if (header && !header.dataset.ready) {
      header.dataset.ready = "true";
      header.closest(".shopify-section")?.classList.add("site-header-section");
      const compactHeaderAt = 48;
      const expandHeaderAt = 8;
      let isScrolled = header.classList.contains("is-scrolled");
      let scrollFrame = null;
      const setScrolled = () => {
        const scrollTop = Math.max(window.scrollY, document.documentElement.scrollTop);

        if (!isScrolled && scrollTop >= compactHeaderAt) isScrolled = true;
        else if (isScrolled && scrollTop <= expandHeaderAt) isScrolled = false;
        else return;

        header.classList.toggle("is-scrolled", isScrolled);
      };
      const requestHeaderUpdate = () => {
        if (scrollFrame !== null) return;
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = null;
          setScrolled();
        });
      };
      setScrolled();
      window.addEventListener("scroll", requestHeaderUpdate, { passive: true });
    }

    $$("[data-mobile-menu-toggle]", scope).forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = "true";
      button.addEventListener("click", () => {
        const menu = $("#MobileMenu");
        if (!menu) return;
        const open = menu.classList.toggle("is-open");
        button.setAttribute("aria-expanded", String(open));
      });
    });

    $$("[data-localized-link]", scope).forEach((link) => {
      if (link.dataset.ready) return;
      link.dataset.ready = "true";
      const target = new URL(link.getAttribute("href"), window.location.origin);
      target.search = window.location.search;
      target.hash = window.location.hash;
      link.href = `${target.pathname}${target.search}${target.hash}`;
    });

    $$("[data-sort-select]", scope).forEach((select) => {
      if (select.dataset.ready) return;
      select.dataset.ready = "true";
      select.addEventListener(
        "change",
        () => select.form && select.form.submit(),
      );
    });
  }

  function initCollectionOverflow(scope = document) {
    $$('[data-collection-overflow]', scope).forEach((navigation) => {
      if (navigation.dataset.collectionOverflowReady) return;
      navigation.dataset.collectionOverflowReady = 'true';
      const more = $('[data-collection-more]', navigation);
      const toggle = $('[data-collection-more-toggle]', navigation);
      const menu = $('[data-collection-more-menu]', navigation);
      if (!more || !toggle || !menu) return;

      let updateFrame;
      const close = () => {
        more.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      };
      const restoreLinks = () => {
        Array.from(menu.children).forEach((link) => more.before(link));
        more.hidden = true;
        close();
      };
      const update = () => {
        window.cancelAnimationFrame(updateFrame);
        updateFrame = window.requestAnimationFrame(() => {
          restoreLinks();
          if (window.matchMedia('(max-width: 989px)').matches) return;
          if (navigation.scrollWidth <= navigation.clientWidth + 1) return;

          more.hidden = false;
          const links = Array.from(navigation.children).filter((item) =>
            item.matches('a[data-collection-id]'),
          );
          while (
            navigation.scrollWidth > navigation.clientWidth + 1 &&
            links.length
          ) {
            menu.prepend(links.pop());
          }
          if (!menu.children.length) more.hidden = true;
        });
      };

      toggle.addEventListener('click', () => {
        const open = more.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
      });
      document.addEventListener('click', (event) => {
        if (!more.contains(event.target)) close();
      });
      more.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        close();
        toggle.focus();
      });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(navigation);
      else window.addEventListener('resize', update, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
      update();
    });
  }

  function initFaq(scope = document) {
    $$("[data-faq-details]", scope).forEach((details) => {
      if (details.dataset.ready) return;
      details.dataset.ready = "true";
      const summary = $("summary", details);
      const answer = $(".faq-answer", details);
      if (!summary || !answer) return;
      let openingFrame;
      let closeTimer;

      const finishClose = () => {
        if (!details.classList.contains("is-expanded")) details.open = false;
        closeTimer = null;
      };

      if (details.open) details.classList.add("is-expanded");

      summary.addEventListener("click", (event) => {
        event.preventDefault();
        if (openingFrame) {
          cancelAnimationFrame(openingFrame);
          openingFrame = null;
        }
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }

        if (details.classList.contains("is-expanded")) {
          details.classList.remove("is-expanded");
          closeTimer = setTimeout(finishClose, 450);
          return;
        }

        details.open = true;
        answer.getBoundingClientRect();
        openingFrame = requestAnimationFrame(() => {
          details.classList.add("is-expanded");
          openingFrame = null;
        });
      });

      answer.addEventListener("transitionend", (event) => {
        if (
          event.target === answer &&
          event.propertyName === "grid-template-rows" &&
          !details.classList.contains("is-expanded")
        ) {
          if (closeTimer) clearTimeout(closeTimer);
          finishClose();
        }
      });
    });
  }

  function initExpandableDescriptions(scope = document) {
    $$('[data-expandable-description]', scope).forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = 'true';
      const content = $('[data-description-content]', root);
      const button = $('[data-description-toggle]', root);
      if (!content || !button) return;

      const sync = () => {
        const lineHeight = Number.parseFloat(
          window.getComputedStyle(content).lineHeight,
        );
        const collapsedHeight = Number.isFinite(lineHeight)
          ? lineHeight * 4
          : 96;
        const fullHeight = content.scrollHeight;
        const isCollapsible = fullHeight > collapsedHeight + 1;
        root.style.setProperty(
          '--product-description-collapsed-height',
          `${collapsedHeight}px`,
        );
        root.style.setProperty(
          '--product-description-expanded-height',
          `${fullHeight}px`,
        );
        root.classList.toggle('is-collapsible', isCollapsible);
        button.hidden = !isCollapsible;
        if (!isCollapsible) {
          root.classList.remove('is-expanded');
          button.setAttribute('aria-expanded', 'false');
          button.textContent = button.dataset.readMore;
        }
      };

      button.addEventListener('click', () => {
        const expanded = root.classList.toggle('is-expanded');
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded
          ? button.dataset.readLess
          : button.dataset.readMore;
        sync();
      });

      requestAnimationFrame(sync);
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(sync);
        observer.observe(content);
      }
    });
  }

  function initArticleShare(scope = document) {
    $$("[data-copy-article-link]", scope).forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = "true";
      button.addEventListener("click", async () => {
        const url = window.location.href;
        try {
          await navigator.clipboard.writeText(url);
        } catch (_) {
          const input = document.createElement("textarea");
          input.value = url;
          input.setAttribute("readonly", "");
          input.style.position = "fixed";
          input.style.opacity = "0";
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          input.remove();
        }
        toast(string("linkCopied"));
      });
    });
  }

  function initHero(scope = document) {
    $$("[data-hero]", scope).forEach((hero) => {
      if (hero.dataset.ready) return;
      hero.dataset.ready = "true";
      const slides = $$("[data-hero-slide]", hero);
      const dots = $$("[data-hero-dot]", hero);
      if (slides.length < 2) return;
      let active = 0;
      let timer;
      const show = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) =>
          slide.classList.toggle("is-active", i === active),
        );
        dots.forEach((dot, i) =>
          dot.classList.toggle("is-active", i === active),
        );
      };
      const play = () => {
        window.clearInterval(timer);
        if (
          hero.dataset.autoplay !== "true" ||
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
        )
          return;
        timer = window.setInterval(
          () => show(active + 1),
          Number(hero.dataset.delay) || 6000,
        );
      };
      dots.forEach((dot) =>
        dot.addEventListener("click", () => {
          show(Number(dot.dataset.heroDot));
          play();
        }),
      );
      $("[data-hero-prev]", hero)?.addEventListener("click", () => {
        show(active - 1);
        play();
      });
      $("[data-hero-next]", hero)?.addEventListener("click", () => {
        show(active + 1);
        play();
      });
      let touchStart = null;
      hero.addEventListener("touchstart", (event) => {
        touchStart = event.touches[0] && event.touches[0].clientX;
      }, { passive: true });
      hero.addEventListener("touchend", (event) => {
        if (touchStart === null) return;
        const end = event.changedTouches[0] && event.changedTouches[0].clientX;
        if (Math.abs(touchStart - end) > 50) show(active + (touchStart > end ? 1 : -1));
        touchStart = null;
        play();
      }, { passive: true });
      hero.addEventListener("mouseenter", () => window.clearInterval(timer));
      hero.addEventListener("mouseleave", play);
      play();
    });
  }

  const isHttpUrl = (value) => {
    if (!value) return false;
    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  function initInstagramFeed(scope = document) {
    $$("[data-instagram-feed]", scope).forEach((grid) => {
      if (grid.dataset.ready) return;
      grid.dataset.ready = "true";

      const supabaseUrl = ((WB.settings && WB.settings.supabaseUrl) || "").replace(/\/+$/, "");
      const publishableKey = WB.settings && WB.settings.supabasePublishableKey;
      if (!supabaseUrl || !publishableKey) return;

      const query = new URLSearchParams({
        select: "id,image_url,caption,post_url",
        active: "eq.true",
        order: "sort_order.asc",
        limit: "6",
      });

      grid.setAttribute("aria-busy", "true");
      fetch(`${supabaseUrl}/rest/v1/instagram_posts?${query}`, {
        headers: {
          Accept: "application/json",
          apikey: publishableKey,
          Authorization: `Bearer ${publishableKey}`,
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Instagram feed: ${response.status}`);
          return response.json();
        })
        .then((posts) => {
          if (!Array.isArray(posts)) return;
          const validPosts = posts.filter((post) => isHttpUrl(post.image_url)).slice(0, 6);
          if (!validPosts.length) return;

          const fragment = document.createDocumentFragment();
          validPosts.forEach((post) => {
            const link = document.createElement("a");
            const destination = isHttpUrl(post.post_url)
              ? post.post_url
              : WB.settings.instagramUrl;
            link.href = destination || "#";
            if (destination) {
              link.target = "_blank";
              link.rel = "noopener noreferrer";
            }

            const image = document.createElement("img");
            image.src = post.image_url;
            image.alt = post.caption || string("instagramPost");
            image.width = 400;
            image.height = 400;
            image.loading = "lazy";
            image.decoding = "async";
            link.appendChild(image);

            if (typeof post.caption === "string" && post.caption.trim()) {
              const caption = document.createElement("span");
              caption.className = "instagram-grid__caption";
              caption.textContent = post.caption.trim();
              link.appendChild(caption);
            }
            fragment.appendChild(link);
          });

          grid.replaceChildren(fragment);
          grid.dataset.source = "supabase";
        })
        .catch((error) => {
          console.warn("Não foi possível carregar o feed do Instagram; usando o fallback do tema.", error);
        })
        .finally(() => grid.removeAttribute("aria-busy"));
    });
  }

  const cartDrawer = {
    node: null,
    cart: null,
    shippingMethod: "shipping",
    pix: false,

    init(scope = document) {
      this.node = $("#CartDrawer");
      if (!this.node) return;
      $$("[data-cart-open]", scope).forEach((button) => {
        if (button.dataset.cartReady) return;
        button.dataset.cartReady = "true";
        button.addEventListener("click", () => this.open());
      });
      if (this.node.dataset.ready) return;
      this.node.dataset.ready = "true";
      $$("[data-cart-close]", this.node).forEach((button) =>
        button.addEventListener("click", () => this.close()),
      );
      $("[data-cart-checkout]", this.node)?.addEventListener("click", () =>
        this.checkout(),
      );
      $("[data-pix-toggle]", this.node)?.addEventListener("change", (event) => {
        this.pix = event.target.checked;
        this.updateSummary();
      });
      $$('input[name="wb_shipping"]', this.node).forEach((radio) =>
        radio.addEventListener("change", () => {
          this.shippingMethod = radio.value;
          $$(".shipping-option", this.node).forEach((label) =>
            label.classList.toggle("is-selected", label.contains(radio)),
          );
          this.updateSummary();
        }),
      );
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !this.node.hidden) this.close();
      });
    },

    async fetchCart() {
      const response = await fetch(endpoint("cart.js"), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Cart request failed");
      this.cart = await this.enforceMinimum(await response.json());
      this.render();
      return this.cart;
    },

    async enforceMinimum(cart) {
      const updates = {};
      (cart.items || []).forEach((item) => {
        const normalized = normalizeQuantity(
          item.quantity,
          cartItemQuantityRule(item),
        );
        if (item.quantity > 0 && item.quantity !== normalized)
          updates[item.key] = normalized;
      });
      if (!Object.keys(updates).length) return cart;
      const response = await fetch(endpoint("cart/update.js"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error("Cart minimum update failed");
      return response.json();
    },

    async open() {
      this.node = this.node || $("#CartDrawer");
      if (!this.node) return;
      this.node.hidden = false;
      this.node.setAttribute("aria-hidden", "false");
      document.body.classList.add("drawer-open");
      const checkoutButton = $("[data-cart-checkout]", this.node);
      if (checkoutButton) checkoutButton.disabled = true;
      try {
        await this.fetchCart();
      } catch (_) {
        toast(string("cartError"), true);
        return;
      }
      try {
        await this.removeUnavailableItems();
        if (checkoutButton) checkoutButton.disabled = false;
      } catch (_) {
        toast(string("inventoryCheckError", string("cartError")), true);
      }
    },

    close() {
      if (!this.node) return;
      this.node.hidden = true;
      this.node.setAttribute("aria-hidden", "true");
      document.body.classList.remove("drawer-open");
    },

    shippingCents() {
      if (!this.cart || this.shippingMethod === "pickup") return 0;
      if (this.cart.item_count > 100) return null;
      return 8000;
    },

    updateSummary() {
      if (!this.cart || !this.node) return;
      const shipping = this.shippingCents();
      const baseTotal = this.cart.total_price + (shipping || 0);
      const total = this.pix ? Math.round(baseTotal * 0.95) : baseTotal;
      const pickupRadio = $('input[value="pickup"]', this.node);
      if (pickupRadio) {
        pickupRadio.disabled = this.cart.item_count > 100;
        if (pickupRadio.disabled && pickupRadio.checked) {
          this.shippingMethod = "shipping";
          $('input[value="shipping"]', this.node).checked = true;
        }
      }
      $("[data-cart-subtotal]", this.node).textContent = money(
        this.cart.total_price,
      );
      $("[data-cart-shipping]", this.node).textContent =
        shipping === null
          ? string("calculateShipping")
          : shipping === 0
            ? string("free")
            : money(shipping);
      $("[data-shipping-price]", this.node).textContent =
        shipping === null ? string("calculateShipping") : money(shipping);
      $("[data-shipping-label]", this.node).textContent =
        shipping === null
          ? string("shippingConsult")
          : string("estimatedShipping");
      $("[data-cart-total]", this.node).textContent = money(total);
      const pixTotal = $("[data-pix-total]", this.node);
      if (pixTotal) {
        pixTotal.hidden = !this.pix;
        pixTotal.textContent = `${string("pix5Total")} ${money(total)}`;
      }
    },

    render() {
      if (!this.cart || !this.node) return;
      $$("[data-cart-count]").forEach((counter) => {
        counter.textContent = String(this.cart.item_count);
        counter.hidden = false;
      });
      const label = $("[data-cart-count-label]", this.node);
      if (label)
        label.textContent = `${this.cart.item_count} ${string("items")}`;
      const items = $("[data-cart-items]", this.node);
      const footer = $("[data-cart-footer]", this.node);
      if (!this.cart.items.length) {
        items.innerHTML = `<div class="drawer__empty"><div><p>${string("cartEmpty")}</p><a class="button button--outline" href="${rootPath}collections/all">${string("continueShopping")}</a></div></div>`;
        footer.hidden = true;
        return;
      }
      footer.hidden = false;
      items.innerHTML = this.cart.items
        .map((item) => {
          const itemRule = cartItemQuantityRule(item);
          const itemQuantity = normalizeQuantity(item.quantity, itemRule);
          const variant =
            item.options_with_values && item.options_with_values.length
              ? item.options_with_values.map((o) => o.value).join(" • ")
              : "";
          const image =
            item.featured_image && item.featured_image.url
              ? `${item.featured_image.url}&width=160`
              : "";
          return `<article class="cart-line" data-cart-line="${item.key}">
          ${image ? `<img class="cart-line__image" src="${image}" alt="">` : '<div class="cart-line__image"></div>'}
          <div><h3><a href="${item.url}">${this.escape(item.product_title)}</a></h3><p class="cart-line__variant">${this.escape(variant)}</p><span class="cart-line__price">${money(item.final_line_price)}</span>
          <div class="cart-line__controls"><button type="button" data-cart-quantity="${normalizeQuantity(itemQuantity - itemRule.step, itemRule)}" aria-label="${string("decrease")}">−</button><span>${itemQuantity}</span><button type="button" data-cart-quantity="${normalizeQuantity(itemQuantity + itemRule.step, itemRule)}" aria-label="${string("increase")}">+</button></div></div>
          <button class="cart-line__remove" type="button" data-cart-remove aria-label="${string("remove")}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 14H7L6 7m4 4v6m4-6v6"></path></svg></button>
        </article>`;
        })
        .join("");
      $$("[data-cart-line]", items).forEach((line) => {
        $$("[data-cart-quantity]", line).forEach((button) =>
          button.addEventListener("click", () =>
            this.change(
              line.dataset.cartLine,
              Number(button.dataset.cartQuantity),
            ),
          ),
        );
        $("[data-cart-remove]", line)?.addEventListener("click", () =>
          this.change(line.dataset.cartLine, 0),
        );
      });
      this.updateSummary();
    },

    async change(key, quantity) {
      try {
        const item = ((this.cart && this.cart.items) || []).find(
          (entry) => entry.key === key,
        );
        const safeQuantity =
          Number(quantity) === 0
            ? 0
            : normalizeQuantity(quantity, cartItemQuantityRule(item));
        const response = await fetch(endpoint("cart/change.js"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ id: key, quantity: safeQuantity }),
        });
        if (!response.ok) throw new Error("Cart change failed");
        this.cart = await response.json();
        this.render();
      } catch (_) {
        toast(string("cartError"), true);
      }
    },

    async unavailableInventoryKeys() {
      const response = await fetch(endpoint("cart.js"), {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Inventory check failed");
      const cart = await response.json();
      const inventory = await fetchStorefrontInventory(
        (cart.items || []).map((item) => item.variant_id),
        true,
      );
      return (cart.items || [])
        .filter((item) => {
          const variantInventory = inventory.get(String(item.variant_id));
          const properties = item.properties || {};
          const customRule =
            properties._wb_rule_handle &&
            properties._wb_rule_handle !== "default";
          const requiredQuantity = customRule
            ? item.quantity
            : Math.max(minimumStockQuantity(), item.quantity);
          return (
            !variantInventory ||
            !variantInventory.availableForSale ||
            variantInventory.quantityAvailable < requiredQuantity
          );
        })
        .map((item) => item.key);
    },

    async removeUnavailableItems(options = {}) {
      const { redirectToHome = false, reloadCurrent = false } = options;
      const keys = await this.unavailableInventoryKeys();
      if (!keys.length) return false;
      const updates = {};
      keys.forEach((key) => {
        updates[key] = 0;
      });
      const response = await fetch(endpoint("cart/update.js"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error("Unavailable product removal failed");
      this.cart = await response.json();
      if (this.node && !this.node.hidden) this.render();
      const message = string("productUnavailable", "Produto indisponível");
      if (redirectToHome || reloadCurrent) {
        try {
          window.sessionStorage.setItem(cartNoticeKey, message);
        } catch (_) {
          /* The navigation still works when storage is unavailable. */
        }
      }
      if (redirectToHome) window.location.href = rootPath;
      else if (reloadCurrent) window.location.reload();
      else toast(message, true);
      return true;
    },

    async checkout() {
      try {
        if (await this.removeUnavailableItems({ redirectToHome: true }))
          return true;
      } catch (_) {
        toast(string("inventoryCheckError", string("cartError")), true);
        return false;
      }
      const attributes = {
        [string("deliveryMethod")]:
          this.shippingMethod === "pickup"
            ? string("pickup")
            : string("shipping"),
      };
      try {
        await fetch(endpoint("cart/update.js"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes }),
        });
      } catch (_) {
        /* Checkout can still continue. */
      }
      analytics("begin_checkout", {
        value: ((this.cart && this.cart.total_price) || 0) / 100,
        currency: "BRL",
      });
      window.location.href = this.pix
        ? `${rootPath}discount/PIX5?redirect=${encodeURIComponent(rootPath + "checkout")}`
        : `${rootPath}checkout`;
      return true;
    },

    escape(value) {
      const div = document.createElement("div");
      div.textContent = value || "";
      return div.innerHTML;
    },
  };

  async function addFormToCart(form, openDrawer = true) {
    const submit = $('[type="submit"]', form);
    if (submit) submit.disabled = true;
    try {
      if (form.dataset.variantPurchasable === "false")
        throw new Error(
          string("productUnavailable", "Produto indisponível"),
        );
      const data = new FormData(form);
      if (data.has("quantity")) {
        const input = $('[name="quantity"]', form);
        data.set(
          "quantity",
          String(
            normalizeQuantity(data.get("quantity"), quantityRuleFromDataset(input)),
          ),
        );
      }
      const response = await fetch(endpoint("cart/add.js"), {
        method: "POST",
        headers: { Accept: "application/json" },
        body: data,
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(
          result.description || result.message || string("addFailed"),
        );
      analytics("add_to_cart", {
        ecommerce: {
          items: [
            {
              item_id: String(result.product_id || result.variant_id),
              item_name: result.product_title,
              price: Number(result.price || 0) / 100,
              quantity: result.quantity,
            },
          ],
        },
      });
      toast(string("added"));
      if (openDrawer) await cartDrawer.open();
      return result;
    } catch (error) {
      toast(
        error.message || string("cartError"),
        true,
      );
      throw error;
    } finally {
      if (submit)
        submit.disabled = form.dataset.variantPurchasable === "false";
    }
  }

  function initAjaxForms(scope = document) {
    $$("form[data-ajax-cart-form], form.wb-ajax-cart-form", scope).forEach(
      (form) => {
        if (form.dataset.ready) return;
        form.dataset.ready = "true";
        form.addEventListener("submit", (event) => {
          event.preventDefault();
          addFormToCart(form).catch(() => {});
        });
      },
    );
  }

  function initCartPage(scope = document) {
    $$('[data-cart-page-form]', scope).forEach((form) => {
      if (form.dataset.cartPageReady) return;
      form.dataset.cartPageReady = "true";
      const initialCheckoutButton = $('[name="checkout"]', form);
      if (initialCheckoutButton) initialCheckoutButton.disabled = true;
      cartDrawer
        .removeUnavailableItems({ reloadCurrent: true })
        .then((removed) => {
          if (!removed && initialCheckoutButton)
            initialCheckoutButton.disabled = false;
        })
        .catch(() => {
          toast(string("inventoryCheckError", string("cartError")), true);
        });
      form.addEventListener("submit", async (event) => {
        if (!event.submitter || event.submitter.name !== "checkout") return;
        event.preventDefault();
        const checkoutButton = event.submitter;
        checkoutButton.disabled = true;
        try {
          const updates = {};
          $$('[data-cart-line-key]', form).forEach((input) => {
            updates[input.dataset.cartLineKey] = normalizeQuantity(
              input.value,
              quantityRuleFromDataset(input),
            );
          });
          const response = await fetch(endpoint("cart/update.js"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ updates }),
          });
          if (!response.ok) throw new Error("Cart update failed");
          cartDrawer.cart = await response.json();
          const checkoutStarted = await cartDrawer.checkout();
          if (!checkoutStarted) checkoutButton.disabled = false;
        } catch (_) {
          toast(string("cartError"), true);
          checkoutButton.disabled = false;
        }
      });
    });
  }

  const colorMap = {
    preto: "#000",
    black: "#000",
    branco: "#fff",
    white: "#fff",
    cinza: "#888",
    gray: "#888",
    grey: "#888",
    verde: "#2d7d46",
    green: "#2d7d46",
    rosa: "#e91e8c",
    pink: "#e91e8c",
    azul: "#2563eb",
    blue: "#2563eb",
    vermelho: "#dc2626",
    red: "#dc2626",
    amarelo: "#eab308",
    yellow: "#eab308",
    marrom: "#8b4513",
    brown: "#8b4513",
    bege: "#f5f0e1",
    beige: "#f5f0e1",
    laranja: "#f97316",
    orange: "#f97316",
    roxo: "#7c3aed",
    purple: "#7c3aed",
    marinho: "#1e3a5f",
    navy: "#1e3a5f",
    caqui: "#c3b091",
    khaki: "#c3b091",
    vinho: "#722f37",
    bordo: "#722f37",
    coral: "#ff7f50",
    creme: "#fffdd0",
    chumbo: "#36454f",
    royal: "#4169e1",
    tiffany: "#0abab5",
    turquesa: "#40e0d0",
  };

  const compoundColorMap = {
    "off white": "#f7f4e9",
    "amarelo ouro": "#f2b705",
    "amarelo limao": "#dfff00",
    "amarelo lima": "#dfff00",
    "azul marinho": "#1e3a5f",
    "azul royal": "#4169e1",
    "azul bebe": "#89cff0",
    "azul claro": "#60a5fa",
    "azul escuro": "#1e3a8a",
    "verde militar": "#4b5320",
    "verde musgo": "#556b2f",
    "verde bandeira": "#009739",
    "verde agua": "#66cdaa",
    "verde limao": "#a4d65e",
    "verde neon": "#39ff14",
    "cinza chumbo": "#36454f",
    "cinza mescla": "#9ca3af",
    "rosa bebe": "#f4c2c2",
    "laranja neon": "#ff5f1f",
  };

  const normalizeColorName = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[-_/]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const resolveSwatchColor = (value) => {
    const normalized = normalizeColorName(value);
    if (!normalized) return "";
    if (colorMap[normalized]) return colorMap[normalized];
    if (compoundColorMap[normalized]) return compoundColorMap[normalized];
    const compoundMatch = Object.keys(compoundColorMap).find((name) =>
      normalized.includes(name),
    );
    if (compoundMatch) return compoundColorMap[compoundMatch];
    const baseMatch = Object.keys(colorMap).find((name) =>
      normalized.split(" ").includes(name),
    );
    return baseMatch ? colorMap[baseMatch] : "";
  };

  const applyColorSwatches = (root, selector) => {
    $$(selector, root).forEach((button) => {
      const inlineColor = button.style.getPropertyValue("--swatch").trim();
      if (inlineColor && CSS.supports("color", inlineColor)) return;
      const mappedColor = resolveSwatchColor(button.dataset.cardOptionValue);
      if (mappedColor) button.style.setProperty("--swatch", mappedColor);
    });
  };

  function initProduct(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = "true";
    const form = $("form.product-form", root);
    const variants = readJson($("[data-product-variants]", root)) || [];
    const context = readJson($("[data-product-context]", root)) || {};
    const rule = quantityRule(context.purchaseRule || {});
    const variantInput = $("[data-variant-id]", root);
    const optionGroups = $$("[data-product-option]", root);
    const mediaItems = $$(".product-media__item[data-media-id]", root);
    const galleryThumbs = $$("[data-gallery-thumb]", root);
    const galleryThumbsContainer = $("[data-gallery-thumbs]", root);
    const colorOptionIndex = optionGroups.findIndex((group) =>
      /cor|color|colour/i.test(group.dataset.optionName || ""),
    );
    const hasLinkedVariant = root.dataset.hasLinkedVariant === "true";
    let currentVariant =
      variants.find(
        (variant) =>
          String(variant.id) === String(variantInput && variantInput.value),
      ) || variants[0];
    if (
      root.dataset.requireSelection === "true" &&
      optionGroups.length &&
      !hasLinkedVariant
    ) {
      currentVariant = null;
      if (variantInput) variantInput.value = "";
      const initialAdd = $("[data-add-to-cart]", root);
      const initialBuy = $("[data-buy-now]", root);
      if (initialAdd) initialAdd.disabled = true;
      if (initialBuy) initialBuy.disabled = true;
    }
    if (form)
      form.dataset.variantPurchasable = String(
        variantIsPurchasable(
          currentVariant,
          rule.custom ? rule.min : Math.max(rule.min, minimumStockQuantity()),
        ),
      );

    $$(".option-value--color", root).forEach((button) => {
      const inlineColor = button.style.getPropertyValue("--swatch").trim();
      if (inlineColor && CSS.supports("color", inlineColor)) return;
      const mappedColor = resolveSwatchColor(button.dataset.optionValue);
      if (mappedColor) button.style.setProperty("--swatch", mappedColor);
    });

    const selectedOptions = () =>
      optionGroups.map(
        (group) =>
          $(".option-value.is-selected", group)?.dataset.optionValue || "",
      );
    const findVariant = () =>
      variants.find(
        (variant) =>
          variant.options &&
          variant.options.every(
            (value, index) => value === selectedOptions()[index],
          ),
      );

    const showMedia = (id) => {
      if (!id) return;
      mediaItems.forEach((item) =>
        item.classList.toggle(
          "is-active",
          String(item.dataset.mediaId) === String(id),
        ),
      );
      galleryThumbs.forEach((thumb) =>
        thumb.classList.toggle(
          "is-active",
          String(thumb.dataset.galleryThumb) === String(id),
        ),
      );
    };

    const variantMediaId = (variant) =>
      variant &&
      ((variant.featured_media && variant.featured_media.id) ||
        (variant.featured_image && variant.featured_image.id));

    const mediaFileFamily = (source) => {
      if (!source) return "";
      let filename = String(source).split("?")[0].split("/").pop() || "";
      try {
        filename = decodeURIComponent(filename);
      } catch (_) {
        /* Keep the original filename when it contains malformed escapes. */
      }
      return filename
        .replace(/\.[^.]+$/, "")
        .replace(/_[0-9a-f]{8}-[0-9a-f-]{27,}$/i, "")
        .replace(/_\d+$/, "")
        .toLocaleLowerCase("pt-BR")
        .replace(/[^a-z0-9]+/g, "");
    };

    const updateVariantGallery = (variant) => {
      if (!galleryThumbsContainer) return;
      const allowedIds = new Set();
      const options = selectedOptions();
      const selectedColor =
        colorOptionIndex >= 0 ? options[colorOptionIndex] : "";

      if (selectedColor) {
        variants
          .filter(
            (item) =>
              item.options && item.options[colorOptionIndex] === selectedColor,
          )
          .forEach((item) => {
            const id = variantMediaId(item);
            if (id) allowedIds.add(String(id));
          });

        const mediaColorOwners = new Map();
        variants.forEach((item) => {
          const id = variantMediaId(item);
          const color = item.options && item.options[colorOptionIndex];
          if (!id || !color) return;
          const key = String(id);
          if (!mediaColorOwners.has(key)) mediaColorOwners.set(key, new Set());
          mediaColorOwners.get(key).add(color);
        });
        let currentMediaOwners = null;
        mediaItems.forEach((item) => {
          const id = String(item.dataset.mediaId);
          if (mediaColorOwners.has(id))
            currentMediaOwners = mediaColorOwners.get(id);
          if (currentMediaOwners && currentMediaOwners.has(selectedColor))
            allowedIds.add(id);
        });

        const colorTokens = selectedColor
          .toLocaleLowerCase("pt-BR")
          .split(/\s+/)
          .filter(Boolean);
        mediaItems.forEach((item) => {
          const alt = String(item.dataset.mediaAlt || "").toLocaleLowerCase(
            "pt-BR",
          );
          if (
            alt &&
            colorTokens.length &&
            colorTokens.every((token) => alt.includes(token))
          )
            allowedIds.add(String(item.dataset.mediaId));
        });

        // Shopify links only one image directly to a variant. Some products keep
        // the remaining views in an unlinked block before the first variant image.
        // A matching filename family identifies the color that owns that block.
        const variantMediaIds = new Set(
          variants.map(variantMediaId).filter(Boolean).map(String),
        );
        const firstVariantMediaIndex = mediaItems.findIndex((item) =>
          variantMediaIds.has(String(item.dataset.mediaId)),
        );
        const leadingGallery =
          firstVariantMediaIndex > 0
            ? mediaItems.slice(0, firstVariantMediaIndex)
            : [];
        const selectedMediaId = String(variantMediaId(variant) || "");
        const selectedMedia = mediaItems.find(
          (item) => String(item.dataset.mediaId) === selectedMediaId,
        );
        const selectedFamily = mediaFileFamily(selectedMedia?.dataset.mediaSrc);
        if (
          selectedFamily &&
          leadingGallery.some(
            (item) => mediaFileFamily(item.dataset.mediaSrc) === selectedFamily,
          )
        ) {
          allowedIds.clear();
          leadingGallery.forEach((item) =>
            allowedIds.add(String(item.dataset.mediaId)),
          );
        }
      } else if (!optionGroups.length || variants.length <= 1) {
        mediaItems.forEach((item) =>
          allowedIds.add(String(item.dataset.mediaId)),
        );
      }

      if (!allowedIds.size) {
        const id = variantMediaId(variant);
        if (id) allowedIds.add(String(id));
      }

      galleryThumbs.forEach((thumb) => {
        thumb.hidden = !allowedIds.has(String(thumb.dataset.galleryThumb));
      });
      const visibleThumbs = galleryThumbs.filter((thumb) => !thumb.hidden);
      galleryThumbsContainer.hidden = visibleThumbs.length <= 1;
      galleryThumbsContainer.scrollLeft = 0;

      if (!allowedIds.size) return;
      const activeMedia = mediaItems.find((item) =>
        item.classList.contains("is-active"),
      );
      if (!activeMedia || !allowedIds.has(String(activeMedia.dataset.mediaId))) {
        const firstMedia = mediaItems.find((item) =>
          allowedIds.has(String(item.dataset.mediaId)),
        );
        if (firstMedia) showMedia(firstMedia.dataset.mediaId);
      }
    };

    const updateSummary = () => {
      const input = $("[data-quantity-input]", root);
      const quantity = normalizeQuantity(input ? input.value : rule.min, rule);
      const unit = Number((currentVariant && currentVariant.price) || (variants[0] && variants[0].price) || 0);
      const shipping = quantity > 100 ? null : 8000;
      const total = unit * quantity + (shipping || 0);
      const setSummaryText = (selector, value) => {
        const element = $(selector, root);
        if (element) element.textContent = value;
      };
      const shippingText =
        shipping === null ? string("calculateShipping") : money(shipping);
      setSummaryText("[data-summary-quantity]", String(quantity));
      setSummaryText("[data-summary-unit]", money(unit));
      setSummaryText("[data-summary-shipping]", shippingText);
      setSummaryText("[data-summary-total]", money(total));
      setSummaryText("[data-summary-pix]", money(Math.round(total * 0.95)));
      setSummaryText("[data-product-shipping]", shippingText);
      $$("[data-quantity-preset]", root).forEach((button) =>
        button.classList.toggle(
          "is-selected",
          Number(button.dataset.quantityPreset) === quantity,
        ),
      );

      const add = $("[data-add-to-cart]", root);
      const buy = $("[data-buy-now]", root);
      const whatsapp = $("[data-whatsapp-product]", root);
      let hideCheckout;
      let hideWhatsapp;
      if (rule.custom) {
        hideCheckout = rule.disableCheckout === true;
        hideWhatsapp = rule.disableWhatsapp === true;
        if (hideCheckout && hideWhatsapp) hideCheckout = false;
      } else {
        hideCheckout = quantity > 30;
        hideWhatsapp = quantity <= 30;
      }
      if (add) add.hidden = hideCheckout;
      if (buy) buy.hidden = hideCheckout;
      if (whatsapp) whatsapp.hidden = hideWhatsapp;
      if (currentVariant && currentVariant.storefront_inventory_checked) {
        const requiredQuantity = rule.custom
          ? quantity
          : Math.max(quantity, minimumStockQuantity());
        if (!currentVariant.storefront_available)
          setVariantPurchaseState("sold-out");
        else if (!variantHasMinimumStock(currentVariant, requiredQuantity))
          setVariantPurchaseState("unavailable");
        else setVariantPurchaseState("available");
      }
    };

    let inventoryRequestId = 0;

    const setVariantPurchaseState = (state) => {
      const add = $("[data-add-to-cart]", root);
      const buy = $("[data-buy-now]", root);
      const addLabel = $("[data-add-label]", root);
      const buyLabel = $("[data-buy-label]", root);
      const purchasable = state === "available";
      if (form) form.dataset.variantPurchasable = String(purchasable);
      if (add) add.disabled = !purchasable;
      if (buy) buy.disabled = !purchasable;

      if (state === "checking") {
        if (addLabel) addLabel.textContent = string("inventoryChecking");
        if (buyLabel) buyLabel.textContent = string("inventoryChecking");
      } else if (state === "error") {
        if (addLabel) addLabel.textContent = string("inventoryCheckError");
        if (buyLabel) buyLabel.textContent = string("inventoryCheckError");
      } else if (state === "unavailable") {
        if (addLabel) addLabel.textContent = string("productUnavailable");
        if (buyLabel) buyLabel.textContent = string("productUnavailable");
      } else if (state === "sold-out") {
        if (addLabel) addLabel.textContent = string("soldOut");
        if (buyLabel) buyLabel.textContent = string("soldOut");
      } else if (state === "available") {
        if (addLabel) addLabel.textContent = string("addToCart");
        if (buyLabel) buyLabel.textContent = string("buyNow");
      }
    };

    const refreshVariantInventory = async (variant) => {
      const requestId = ++inventoryRequestId;
      if (!variant) return;
      setVariantPurchaseState("checking");
      try {
        const inventory = await fetchStorefrontInventory([variant.id]);
        if (requestId !== inventoryRequestId || currentVariant !== variant) return;
        const variantInventory = inventory.get(String(variant.id));
        if (!variantInventory)
          throw new Error("Variant inventory is unavailable");
        applyStorefrontInventory(variant, variantInventory);
        if (!variantInventory.availableForSale)
          setVariantPurchaseState("sold-out");
        else if (
          !variantHasMinimumStock(
            variant,
            rule.custom
              ? normalizeQuantity($("[data-quantity-input]", root)?.value, rule)
              : Math.max(
                  normalizeQuantity($("[data-quantity-input]", root)?.value, rule),
                  minimumStockQuantity(),
                ),
          )
        )
          setVariantPurchaseState("unavailable");
        else setVariantPurchaseState("available");
      } catch (_) {
        if (requestId !== inventoryRequestId || currentVariant !== variant) return;
        setVariantPurchaseState("error");
      }
    };

    const updateVariant = () => {
      currentVariant = findVariant();
      updateVariantGallery(currentVariant);
      const price = $("[data-product-price]", root);
      const compare = $("[data-compare-price]", root);
      const add = $("[data-add-to-cart]", root);
      const buy = $("[data-buy-now]", root);
      const buyLabel = $("[data-buy-label]", root);
      if (!currentVariant) {
        inventoryRequestId += 1;
        if (variantInput) variantInput.value = "";
        if (form) form.dataset.variantPurchasable = "false";
        if (add) add.disabled = true;
        if (buy) buy.disabled = true;
        if (buyLabel) buyLabel.textContent = string("selectOption");
        const summary = $("[data-order-summary]", root);
        if (summary) summary.hidden = true;
        return;
      }
      const summary = $("[data-order-summary]", root);
      if (summary) summary.hidden = false;
      if (variantInput) variantInput.value = currentVariant.id;
      if (price) price.textContent = money(currentVariant.price);
      if (compare) {
        compare.hidden = !(
          currentVariant.compare_at_price > currentVariant.price
        );
        compare.textContent = currentVariant.compare_at_price
          ? money(currentVariant.compare_at_price)
          : "";
      }
      if (currentVariant.featured_media)
        showMedia(currentVariant.featured_media.id);
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?variant=${currentVariant.id}`,
      );
      updateSummary();
      refreshVariantInventory(currentVariant);
    };

    $$("[data-option-position]", root).forEach((button) =>
      button.addEventListener("click", () => {
        const group = button.closest("[data-product-option]");
        $$(".option-value", group).forEach((item) =>
          item.classList.remove("is-selected"),
        );
        button.classList.add("is-selected");
        $("[data-selected-option]", group).textContent =
          button.dataset.optionValue;
        updateVariant();
      }),
    );

    $$("[data-gallery-thumb]", root).forEach((thumb) =>
      thumb.addEventListener("click", () =>
        showMedia(thumb.dataset.galleryThumb),
      ),
    );
    galleryThumbsContainer?.addEventListener(
      "wheel",
      (event) => {
        if (
          galleryThumbsContainer.scrollWidth <=
          galleryThumbsContainer.clientWidth
        )
          return;
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
        event.preventDefault();
        galleryThumbsContainer.scrollBy({
          left: event.deltaY,
          behavior: "smooth",
        });
      },
      { passive: false },
    );
    $("[data-share-product]", root)?.addEventListener("click", async () => {
      const share = {
        title: context.title || document.title,
        url: window.location.href,
      };
      try {
        if (navigator.share) await navigator.share(share);
        else {
          await navigator.clipboard.writeText(share.url);
          toast(string("linkCopied"));
        }
      } catch (_) {
        /* user cancelled */
      }
    });

    const quantityInput = $("[data-quantity-input]", root);
    if (quantityInput) {
      const requestedQuantity = new URLSearchParams(window.location.search).get(
        "quantity",
      );
      if (requestedQuantity)
        quantityInput.value = normalizeQuantity(requestedQuantity, rule);
      const commit = () => {
        quantityInput.value = normalizeQuantity(quantityInput.value, rule);
        updateSummary();
      };
      quantityInput.addEventListener("change", commit);
      quantityInput.addEventListener("blur", commit);
      quantityInput.addEventListener("input", updateSummary);
    }
    $$("[data-quantity-preset]", root).forEach((button) =>
      button.addEventListener("click", () => {
        quantityInput.value = button.dataset.quantityPreset;
        updateSummary();
      }),
    );
    $$(".shipping-option input", form).forEach((input) =>
      input.addEventListener("change", () =>
        $$(".shipping-option", form).forEach((label) =>
          label.classList.toggle("is-selected", label.contains(input)),
        ),
      ),
    );

    $("[data-buy-now]", root)?.addEventListener("click", async () => {
      if (!currentVariant || !form) return;
      try {
        await addFormToCart(form, false);
        await cartDrawer.fetchCart();
        await cartDrawer.checkout();
      } catch (_) {
        /* handled */
      }
    });

    initCustomizer(
      root,
      context,
      () => currentVariant,
      () =>
        mediaItems.find((item) => item.classList.contains("is-active"))
          ?.dataset.mediaSrc || context.image,
    );
    updateVariantGallery(currentVariant);
    updateSummary();
    refreshVariantInventory(currentVariant);
  }

  function initFavorites(scope = document) {
    const favorites = () => {
      const list = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith("fav_")) continue;
        try {
          const item = JSON.parse(localStorage.getItem(key));
          if (item && item.handle) list.push(item);
        } catch (_) {
          /* ignore */
        }
      }
      return list;
    };
    const refreshCount = () => {
      const count = favorites().length;
      $$("[data-favorite-count]").forEach((node) => {
        node.textContent = String(count);
        node.hidden = count === 0;
      });
    };
    $$("[data-favorite-toggle]", scope).forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = "true";
      const key = `fav_${button.dataset.handle}`;
      const sync = () =>
        button.classList.toggle(
          "is-active",
          Boolean(localStorage.getItem(key)),
        );
      sync();
      button.addEventListener("click", () => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          toast(string("favoriteRemoved"));
        } else {
          localStorage.setItem(
            key,
            JSON.stringify({
              handle: button.dataset.handle,
              title: button.dataset.title,
              image: button.dataset.image,
            }),
          );
          analytics("add_to_wishlist", {
            item_id: button.dataset.handle,
            item_name: button.dataset.title,
          });
          toast(string("favoriteAdded"));
        }
        sync();
        refreshCount();
      });
    });
    const grid = $("[data-favorites-grid]", scope);
    if (grid && !grid.dataset.ready) {
      grid.dataset.ready = "true";
      const render = () => {
        const list = favorites();
        if (!list.length) {
          grid.innerHTML =
            '<div class="favorites-empty"><span aria-hidden="true">♡</span><p>' + string("favoritesEmpty") + '</p><small>' + string("favoritesEmptyHelp") + '</small><a class="button button--primary" href="' + rootPath + 'collections/all">' + string("viewProducts") + '</a></div>';
          return;
        }
        grid.innerHTML = list
          .map(
            (item) =>
              `<article class="favorite-card"><button class="favorite-card__remove" type="button" data-remove-favorite="${item.handle}" aria-label="${string("remove")}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"></path></svg></button><a href="${rootPath}products/${item.handle}">${item.image ? `<img src="${item.image}" alt="">` : ""}<div class="favorite-card__body"><h3>${cartDrawer.escape(item.title)}</h3></div></a></article>`,
          )
          .join("");
        $$("[data-remove-favorite]", grid).forEach((button) =>
          button.addEventListener("click", () => {
            localStorage.removeItem(`fav_${button.dataset.removeFavorite}`);
            render();
            refreshCount();
          }),
        );
      };
      render();
    }
    refreshCount();
    window.addEventListener("storage", refreshCount);
  }

  function initProductCards(scope = document) {
    $$('[data-product-card]', scope).forEach((card) => {
      if (card.dataset.cardReady) return;
      card.dataset.cardReady = 'true';
      applyColorSwatches(card, '.card-swatch--color');
      const track = $('.product-card__slides', card);
      const images = $$('.product-card__image', card);
      const dots = $$('[data-card-dot]', card);
      const variants = readJson($('[data-card-variants]', card)) || [];
      const productUrl = card.dataset.productUrl;
      const cardRule = quantityRuleFromDataset(card);
      const updateProductLinks = (variant) => {
        if (!productUrl || !variant) return;
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('variant', String(variant.id));
        url.searchParams.set(
          'quantity',
          String(
            normalizeQuantity(
              $('[data-card-quantity]', card)?.textContent,
              cardRule,
            ),
          ),
        );
        $$('[data-card-product-link]', card).forEach((link) => {
          link.href = `${url.pathname}${url.search}${url.hash}`;
        });
      };
      const updateVariant = (fallbackVariantId) => {
        const selected = [];
        $$('[data-card-option].is-selected', card).forEach((item) => {
          selected[Number(item.dataset.cardOptionPosition)] = item.dataset.cardOptionValue;
        });
        const selectedCount = selected.filter(Boolean).length;
        const variant =
          variants.find(
            (item) =>
              selectedCount === item.options?.length &&
              item.options.every((value, index) => value === selected[index]),
          ) ||
          variants.find((item) => String(item.id) === String(fallbackVariantId)) ||
          variants[0];
        if (!variant) return;
        updateProductLinks(variant);
        const price = $('[data-product-price]', card);
        const compare = $('[data-compare-price]', card);
        if (price) price.textContent = money(variant.price);
        if (compare) {
          compare.hidden = !(variant.compare_at_price > variant.price);
          compare.textContent = variant.compare_at_price ? money(variant.compare_at_price) : '';
        }
      };
      let active = images.findIndex((image) => image.classList.contains('is-active'));
      if (active < 0) active = 0;
      const updateDots = (index) => {
        dots.forEach((dot, i) => {
          const isActive = i === index;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-pressed', String(isActive));
        });
      };
      const syncColor = (index) => {
        const colorValue = images[index]?.dataset.cardColorValue;
        if (colorValue) {
          $$('[data-card-color="true"]', card).forEach((button) => {
            const isSelected = button.dataset.cardOptionValue === colorValue;
            button.classList.toggle('is-selected', isSelected);
            button.setAttribute('aria-pressed', String(isSelected));
          });
        }
        updateVariant(images[index]?.dataset.cardVariantId);
      };
      const goTo = (index, syncSelection = true) => {
        if (!track || !images.length) return;
        active = (index + images.length) % images.length;
        images.forEach((image, i) => image.classList.toggle('is-active', i === active));
        updateDots(active);
        if (syncSelection) syncColor(active);
      };
      if (images.length) goTo(active);
      $('[data-card-prev]', card)?.addEventListener('click', (event) => {
        event.preventDefault(); event.stopPropagation(); goTo(active - 1);
      });
      $('[data-card-next]', card)?.addEventListener('click', (event) => {
        event.preventDefault(); event.stopPropagation(); goTo(active + 1);
      });
      dots.forEach((dot) => dot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        goTo(Number(dot.dataset.cardDot));
      }));
      let touchStart = null;
      let swiped = false;
      track?.addEventListener('touchstart', (event) => {
        touchStart = event.touches[0]?.clientX ?? null;
      }, { passive: true });
      track?.addEventListener('touchend', (event) => {
        if (touchStart === null) return;
        const touchEnd = event.changedTouches[0]?.clientX ?? touchStart;
        const distance = touchStart - touchEnd;
        touchStart = null;
        if (Math.abs(distance) < 35) return;
        swiped = true;
        goTo(active + (distance > 0 ? 1 : -1));
        window.setTimeout(() => { swiped = false; }, 0);
      }, { passive: true });
      track?.addEventListener('click', (event) => {
        if (swiped) event.preventDefault();
      });
      $$('[data-card-option]', card).forEach((button) => button.addEventListener('click', (event) => {
        event.preventDefault(); event.stopPropagation();
        const group = button.closest('.card-option__values');
        $$('.card-swatch', group).forEach((item) => {
          item.classList.remove('is-selected');
          item.setAttribute('aria-pressed', 'false');
        });
        button.classList.add('is-selected');
        button.setAttribute('aria-pressed', 'true');
        if (button.dataset.cardColor === 'true') {
          const mediaIndex = images.findIndex(
            (image) => image.dataset.cardColorValue === button.dataset.cardOptionValue,
          );
          if (mediaIndex >= 0) goTo(mediaIndex, false);
        }
        updateVariant(images[active]?.dataset.cardVariantId);
      }));
      const quantity = $('[data-card-quantity]', card);
      const changeQuantity = (delta) => {
        if (!quantity) return;
        quantity.textContent = String(
          normalizeQuantity(Number(quantity.textContent || cardRule.min) + delta, cardRule),
        );
        const activeVariantId = images[active]?.dataset.cardVariantId;
        updateVariant(activeVariantId);
      };
      $('[data-card-minus]', card)?.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); changeQuantity(-cardRule.step); });
      $('[data-card-plus]', card)?.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); changeQuantity(cardRule.step); });
    });
  }

  function initProductShowcases(scope = document) {
    $$('[data-product-showcase]', scope).forEach((showcase) => {
      if (showcase.dataset.showcaseReady) return;
      showcase.dataset.showcaseReady = 'true';
      const track = $('[data-showcase-track]', showcase);
      const move = (direction) => track?.scrollBy({ left: direction * Math.min(track.clientWidth * .85, 820), behavior: 'smooth' });
      $('[data-showcase-prev]', showcase)?.addEventListener('click', () => move(-1));
      $('[data-showcase-next]', showcase)?.addEventListener('click', () => move(1));
    });
  }

  function initCollectionCarousels(scope = document) {
    $$('[data-collection-carousel]', scope).forEach((carousel) => {
      if (carousel.dataset.collectionReady) return;
      carousel.dataset.collectionReady = 'true';
      const track = $('[data-collection-track]', carousel);
      const previous = $('[data-collection-prev]', carousel);
      const next = $('[data-collection-next]', carousel);
      if (!track) return;

      let updateFrame;
      const getContentWidth = () => {
        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const padding = (Number.parseFloat(styles.paddingLeft) || 0) + (Number.parseFloat(styles.paddingRight) || 0);
        const items = Array.from(track.children);
        const itemsWidth = items.reduce(
          (total, item) => total + Math.max(item.getBoundingClientRect().width, item.scrollWidth),
          0,
        );
        return itemsWidth + Math.max(0, items.length - 1) * gap + padding;
      };
      const update = () => {
        window.cancelAnimationFrame(updateFrame);
        updateFrame = window.requestAnimationFrame(() => {
          const wasSlider = carousel.classList.contains('has-overflow');
          const contentWidth = getContentWidth();
          const threshold = wasSlider ? 32 : 8;
          const overflow = contentWidth > track.clientWidth - threshold;
          carousel.classList.toggle('has-overflow', overflow);

          const trackBounds = track.getBoundingClientRect();
          const firstItemBounds = track.firstElementChild?.getBoundingClientRect();
          const lastItemBounds = track.lastElementChild?.getBoundingClientRect();
          const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
          const scrollLeft = Math.max(0, track.scrollLeft);
          const edgeTolerance = 8;
          const atStart = scrollLeft <= edgeTolerance || (firstItemBounds && firstItemBounds.left >= trackBounds.left - edgeTolerance);
          const atEnd = maxScrollLeft - scrollLeft <= edgeTolerance || (lastItemBounds && lastItemBounds.right <= trackBounds.right + edgeTolerance);
          const setArrowState = (arrow, disabled) => {
            if (!arrow) return;
            arrow.hidden = !overflow;
            arrow.disabled = disabled;
            arrow.classList.toggle('is-disabled', disabled);
            arrow.setAttribute('aria-disabled', String(disabled));
          };
          setArrowState(previous, atStart);
          setArrowState(next, atEnd);
        });
      };
      const move = (direction) => {
        track.scrollBy({
          left: direction * Math.max(260, track.clientWidth * .72),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      };

      previous?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      track.addEventListener('scroll', update, { passive: true });
      track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      else window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  function initCustomizer(root, context, getVariant, getProductImageUrl) {
    const input = $("[data-logo-input]", root);
    const open = $("[data-customizer-open]", root);
    const modal = $("[data-customizer-modal]", root);
    const canvas = $("[data-customizer-canvas]", root);
    if (!input || !open || !modal || !canvas) return;
    const phoneInput = $("[data-art-phone]", modal);
    const activePhoneProfile = phoneProfile(WB.locale);
    const validatePhone = (markInvalid = false) => {
      if (!phoneInput) return true;
      const digits = phoneNationalDigits(phoneInput.value, activePhoneProfile);
      const valid =
        !digits.length ||
        (digits.length >= activePhoneProfile.minDigits &&
          digits.length <= activePhoneProfile.maxDigits);
      phoneInput.setCustomValidity(valid ? "" : string("phoneInvalid"));
      if (markInvalid && !valid) phoneInput.setAttribute("aria-invalid", "true");
      else phoneInput.removeAttribute("aria-invalid");
      return valid;
    };
    if (phoneInput) {
      phoneInput.addEventListener("input", () => {
        phoneInput.value = formatPhone(phoneInput.value, activePhoneProfile);
        validatePhone();
      });
      phoneInput.addEventListener("blur", () => validatePhone(true));
    }
    const ctx = canvas.getContext("2d");
    const state = {
      logoBase64: null,
      logoName: null,
      logoMime: null,
      logoImage: null,
      productImage: null,
      x: 0.5,
      y: 0.5,
      scale: 0.35,
      logoColor: null,
      text: "",
      textColor: "#ffffff",
      textSize: 42,
      dragging: false,
      generatedImage: null,
    };
    let loadedProductImageUrl = "";
    let productImageRequest = 0;

    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });

    const drawCover = (image) => {
      const scale = Math.max(
        canvas.width / image.width,
        canvas.height / image.height,
      );
      const width = image.width * scale,
        height = image.height * scale;
      ctx.drawImage(
        image,
        (canvas.width - width) / 2,
        (canvas.height - height) / 2,
        width,
        height,
      );
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (state.productImage) drawCover(state.productImage);
      if (state.logoImage) {
        const maxWidth = canvas.width * state.scale;
        const ratio = state.logoImage.height / state.logoImage.width;
        const width = maxWidth,
          height = maxWidth * ratio;
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,.35)";
        ctx.shadowBlur = 6;
        if (state.logoColor) {
          const offscreen = document.createElement("canvas");
          offscreen.width = Math.max(1, Math.round(width));
          offscreen.height = Math.max(1, Math.round(height));
          const offscreenContext = offscreen.getContext("2d");
          offscreenContext.drawImage(
            state.logoImage,
            0,
            0,
            offscreen.width,
            offscreen.height,
          );
          offscreenContext.globalCompositeOperation = "source-in";
          offscreenContext.fillStyle = state.logoColor;
          offscreenContext.fillRect(0, 0, offscreen.width, offscreen.height);
          ctx.drawImage(
            offscreen,
            state.x * canvas.width - width / 2,
            state.y * canvas.height - height / 2,
            width,
            height,
          );
        } else {
          ctx.drawImage(
            state.logoImage,
            state.x * canvas.width - width / 2,
            state.y * canvas.height - height / 2,
            width,
            height,
          );
        }
        ctx.restore();
        if (state.text) {
          ctx.save();
          ctx.font = "800 " + state.textSize + "px Raleway, sans-serif";
          ctx.fillStyle = state.textColor;
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.shadowColor = "rgba(0,0,0,.55)";
          ctx.shadowBlur = 5;
          ctx.fillText(
            state.text,
            state.x * canvas.width,
            state.y * canvas.height + height / 2 + 16,
            canvas.width * 0.8,
          );
          ctx.restore();
        }
      }
    };

    const selectedProductImageUrl = () =>
      (getProductImageUrl && getProductImageUrl()) || context.image;
    const syncProductImage = async () => {
      const imageUrl = selectedProductImageUrl();
      if (!imageUrl || imageUrl === loadedProductImageUrl) return;
      const requestId = ++productImageRequest;
      state.productImage = null;
      state.generatedImage = null;
      draw();
      try {
        const image = await loadImage(imageUrl);
        if (requestId !== productImageRequest) return;
        state.productImage = image;
        loadedProductImageUrl = imageUrl;
        draw();
      } catch (_) {
        /* Keep the canvas available if the product image cannot be loaded. */
      }
    };

    syncProductImage();
    input.addEventListener("change", () => {
      const file = input.files && input.files[0];
      if (!file) return;
      if (file.size > 7.5 * 1024 * 1024) {
        toast(string("fileTooLarge"), true);
        input.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        state.logoBase64 = reader.result;
        state.logoName = file.name;
        state.logoMime = file.type;
        $("[data-logo-status]", root).textContent = `${file.name} ${string("fileSelected")}`;
        open.disabled = false;
        if (file.type.startsWith("image/")) {
          try {
            state.logoImage = await loadImage(reader.result);
            draw();
          } catch (_) {
            /* submission still available */
          }
        }
      };
      reader.readAsDataURL(file);
    });

    const close = () => {
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      document.body.classList.remove("drawer-open");
    };
    open.addEventListener("click", () => {
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("drawer-open");
      syncProductImage();
    });
    $$("[data-customizer-close]", modal).forEach((button) =>
      button.addEventListener("click", close),
    );
    $("[data-logo-scale]", modal)?.addEventListener("input", (event) => {
      state.scale = Number(event.target.value) / 100;
      draw();
    });
    $("[data-logo-color]", modal)?.addEventListener("input", (event) => {
      if (!$("[data-logo-original]", modal).checked)
        state.logoColor = event.target.value;
      draw();
    });
    $("[data-logo-original]", modal)?.addEventListener("change", (event) => {
      state.logoColor = event.target.checked
        ? null
        : $("[data-logo-color]", modal).value;
      draw();
    });
    $("[data-customizer-text]", modal)?.addEventListener("input", (event) => {
      state.text = event.target.value;
      draw();
    });
    $("[data-text-color]", modal)?.addEventListener("input", (event) => {
      state.textColor = event.target.value;
      draw();
    });
    $("[data-text-size]", modal)?.addEventListener("input", (event) => {
      state.textSize = Number(event.target.value);
      draw();
    });

    const pointer = (event) => {
      const box = canvas.getBoundingClientRect();
      state.x = Math.max(
        0,
        Math.min(1, (event.clientX - box.left) / box.width),
      );
      state.y = Math.max(
        0,
        Math.min(1, (event.clientY - box.top) / box.height),
      );
      draw();
    };
    canvas.addEventListener("pointerdown", (event) => {
      state.dragging = true;
      canvas.setPointerCapture(event.pointerId);
      pointer(event);
    });
    canvas.addEventListener("pointermove", (event) => {
      if (state.dragging) pointer(event);
    });
    canvas.addEventListener("pointerup", () => {
      state.dragging = false;
    });

    $("[data-download-preview]", modal)?.addEventListener("click", () => {
      const link = document.createElement("a");
      link.download = `mockup-${context.handle}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    });

    $("[data-generate-mockup]", modal)?.addEventListener(
      "click",
      async (event) => {
        if (!state.logoBase64 || !WB.settings.generateMockupEndpoint) return;
        const button = event.currentTarget;
        button.disabled = true;
        button.textContent = string("generating");
        try {
          const correction = $("[data-ai-correction]", modal)?.value.trim();
          const requestBody = {
            productImageUrl: selectedProductImageUrl(),
            logoBase64: state.logoBase64,
          };
          if (correction && state.generatedImage) {
            requestBody.correction = correction;
            requestBody.previousMockup = state.generatedImage;
          }
          const response = await fetch(WB.settings.generateMockupEndpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: WB.settings.supabasePublishableKey,
              Authorization: `Bearer ${WB.settings.supabasePublishableKey}`,
            },
            body: JSON.stringify(requestBody),
          });
          const result = await response.json();
          if (!response.ok || !result.mockupUrl)
            throw new Error(result.error || string("mockupError"));
          state.productImage = await loadImage(result.mockupUrl);
          state.logoImage = null;
          state.generatedImage = result.mockupUrl;
          draw();
          toast(string("mockupGenerated"));
        } catch (error) {
          toast(error.message, true);
        } finally {
          button.disabled = false;
          button.textContent = state.generatedImage
            ? string("applyAiCorrection")
            : string("generateMockup");
        }
      },
    );

    $("[data-submit-art]", modal)?.addEventListener("click", async (event) => {
      if (!validatePhone(true)) {
        phoneInput.focus();
        phoneInput.reportValidity();
        return;
      }
      if (!state.logoBase64 || !WB.settings.submitArtEndpoint) {
        toast(string("selectLogo"), true);
        return;
      }
      const button = event.currentTarget;
      const status = $("[data-art-status]", modal);
      button.disabled = true;
      status.textContent = string("sendingArt");
      const variant = getVariant();
      const selectedOptions = {};
      if (variant && variant.options)
        variant.options.forEach((value, index) => {
          selectedOptions[`${string("optionLabel")} ${index + 1}`] = value;
        });
      const quantity = normalizeQuantity(
        $("[data-quantity-input]", root)?.value || context.purchaseRule?.min || 30,
        context.purchaseRule || {},
      );
      const payload = {
        product: { handle: context.handle, title: context.title },
        variant: {
          id: variant && String(variant.id),
          title: variant && variant.title,
          selectedOptions,
        },
        quantity,
        logo: {
          filename: state.logoName,
          base64: state.logoBase64,
          mimeType: state.logoMime,
        },
        preview: {
          base64: canvas.toDataURL("image/png", 0.88),
          mimeType: "image/png",
        },
        customer: {
          name: $("[data-art-name]", modal).value,
          email: $("[data-art-email]", modal).value,
          phone: $("[data-art-phone]", modal).value,
          shopifyCustomerId: context.customer && String(context.customer.id),
        },
        notes: [
          $("[data-art-notes]", modal).value,
          state.text
            ? string("textPrefix") + " " +
              state.text +
              " (" +
              state.textColor +
              ", " +
              state.textSize +
              "px)"
            : "",
          state.logoColor ? string("logoColorPrefix") + " " + state.logoColor : "",
        ]
          .filter(Boolean)
          .join(" | "),
      };
      try {
        const response = await fetch(WB.settings.submitArtEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: WB.settings.supabasePublishableKey,
            Authorization: `Bearer ${WB.settings.supabasePublishableKey}`,
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (!response.ok || result.error)
          throw new Error(result.error || string("submitError"));
        const artInput = $("[data-art-submission-id]", root);
        artInput.value = result.id;
        artInput.disabled = false;
        status.textContent = string("artSentStatus");
        analytics("generate_lead", {
          method: "art_submission",
          art_submission_id: result.id,
        });
        toast(string("artSent"));
      } catch (error) {
        status.textContent = error.message;
        toast(error.message, true);
      } finally {
        button.disabled = false;
      }
    });
  }

  const defaultCollectionOrder = [
    "477512138788", // Bonés
    "478019158052", // Chapéus
    "478018863140", // Chaveiros
    "478019125284", // Copos
    "478018830372", // Ecobag
    "484174069796", // Agro
  ];
  const configuredCollectionOrder =
    WB.settings && Array.isArray(WB.settings.collectionOrder)
      ? WB.settings.collectionOrder.map(String).filter(Boolean)
      : [];
  const collectionOrderById = new Map(
    (configuredCollectionOrder.length
      ? configuredCollectionOrder
      : defaultCollectionOrder
    ).map((id, index) => [id, index]),
  );
  const capsCollectionId = "477512138788";
  const hatsCollectionId = "478019158052";

  const reorderElements = (container, selector, priorityFor) => {
    if (!container || container.dataset.catalogOrderReady) return;
    container.dataset.catalogOrderReady = "true";
    const items = $$(selector, container);
    if (items.length < 2) return;
    const orderedItems = items
      .map((item, index) => ({ item, index, priority: priorityFor(item) }))
      .sort((left, right) =>
        left.priority === right.priority
          ? left.index - right.index
          : left.priority - right.priority,
      )
      .map(({ item }) => item);
    const marker = document.createComment("catalog-order");
    items[0].before(marker);
    orderedItems.forEach((item) => marker.before(item));
    marker.remove();
    const limit = Number(
      container.dataset.collectionLimit || container.dataset.productLimit,
    );
    if (Number.isFinite(limit) && limit > 0)
      orderedItems.slice(limit).forEach((item) => item.remove());
  };

  function initCatalogOrdering(scope = document) {
    $$('[data-collection-order-container]', scope).forEach((container) =>
      reorderElements(container, '[data-collection-id]', (item) => {
        const id = String(item.dataset.collectionId || "");
        return collectionOrderById.has(id)
          ? collectionOrderById.get(id)
          : 101;
      }),
    );
    $$('[data-product-order-container]', scope).forEach((container) =>
      reorderElements(container, '[data-product-card]', (item) => {
        const collectionIds = String(item.dataset.productCollectionIds || "")
          .split(",")
          .filter(Boolean);
        const handle = normalizeColorName(item.dataset.productHandle);
        const type = normalizeColorName(item.dataset.productType);
        const tags = normalizeColorName(item.dataset.productTags);
        const category = normalizeColorName(item.dataset.productCategory);
        const isTrucker =
          handle.includes("trucker") ||
          tags.includes("trucker") ||
          category.includes("caminhoneiro");
        if (isTrucker) return 1;
        const isCap =
          collectionIds.includes(capsCollectionId) ||
          handle === "bone" ||
          handle.startsWith("bone ") ||
          type.includes("bone") ||
          tags.includes("bone") ||
          category.includes("bone");
        if (isCap) return 0;
        const isHat =
          collectionIds.includes(hatsCollectionId) ||
          handle.includes("chapeu") ||
          handle.includes("bucket") ||
          category.includes("chapeu");
        return isHat ? 2 : 3;
      }),
    );
    $$('.product-showcase--compact-tail .product-grid', scope).forEach((grid) => {
      const cards = $$(':scope > [data-product-card]', grid);
      const compactCount = cards.length >= 4 ? cards.length % 4 : 0;
      if (!compactCount) return;
      grid.classList.add('has-compact-product-tail');
      grid.classList.add('has-compact-product-lead');
      grid.classList.add(`compact-product-tail--${compactCount}`);
      const compactCards = cards.slice(-compactCount);
      compactCards[0].classList.add('product-card--compact-tail-first');
      compactCards.forEach((card) =>
        card.classList.add('product-card--compact-tail'),
      );
    });
  }

  function initAll(scope = document) {
    initCatalogOrdering(scope);
    initHeader(scope);
    initCollectionOverflow(scope);
    initFaq(scope);
    initExpandableDescriptions(scope);
    initArticleShare(scope);
    initHero(scope);
    initInstagramFeed(scope);
    cartDrawer.init(scope);
    initMinimumQuantityInputs(scope);
    initCartPage(scope);
    initAjaxForms(scope);
    initFavorites(scope);
    initProductCards(scope);
    initProductShowcases(scope);
    initCollectionCarousels(scope);
    $$("[data-lead]", scope).forEach((link) => {
      if (link.dataset.ready) return;
      link.dataset.ready = "true";
      link.addEventListener("click", () =>
        analytics("generate_lead", { method: link.dataset.lead }),
      );
    });
    $$("[data-product-root]", scope).forEach(initProduct);
    showPendingCartNotice();
  }

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => initAll());
  else initAll();
  document.addEventListener("shopify:section:load", (event) =>
    initAll(event.target),
  );
})();
